/**
 * 购物车推荐商品组件
 * 支持基于购物车商品的智能推荐和系统热门商品推荐
 */

class CartRecommendations {
    constructor(containerId, options = {}) {
        this.containerId = containerId;
        this.container = document.getElementById(containerId);
        this.swiper = null; // 存储Swiper实例
        this.options = {
            limit: options.limit || 8,
            showPrice: options.showPrice !== false,
            cssPrefix: 'recommendations',
            cartItems: options.cartItems || [],
            shopLocale: options.shopLocale || 'en',
            shopCurrency: options.shopCurrency || 'USD',
            ...options
        };

        if (this.container) {
            this.init();
        }
    }

    init() {
        const product_id = localStorage.getItem('recently-viewed-products');
        
        // 如果没有最近浏览的产品，随机获取一个产品
        if (!product_id) {
            this.getRandomProductAndRecommend();
            return;
        }
        
        const recommendationsUrl = `/recommendations/products.json?product_id=${JSON.parse(product_id)[0]}&limit=${this.options.limit}`;

        fetch(recommendationsUrl)
            .then(response => response.json())
            .then(data => this.handleProductsData(data))
            .catch(error => this.handleError(error));
    }

    /**
     * 获取随机产品并基于它获取推荐
     */
    getRandomProductAndRecommend() {
        const fallbackUrl = `${window.Shopify.routes.root}collections/all/products.json?limit=50`;
        
        fetch(fallbackUrl)
            .then(response => response.json())
            .then(data => {
                const products = data.products || [];
                if (products && products.length > 0) {
                    // 随机选择一个产品
                    const randomIndex = Math.floor(Math.random() * products.length);
                    const randomProduct = products[randomIndex];
                    
                    // 基于随机产品获取推荐
                    const recommendationsUrl = `/recommendations/products.json?product_id=${randomProduct.id}&limit=${this.options.limit}`;
                    
                    return fetch(recommendationsUrl)
                        .then(response => response.json())
                        .then(data => this.handleProductsData(data))
                        .catch(error => {
                            // 如果推荐API失败，使用备用方案
                            console.warn('Failed to get recommendations, using fallback');
                            this.tryFallbackRecommendations();
                        });
                } else {
                    // 如果没有产品，使用备用方案
                    this.tryFallbackRecommendations();
                }
            })
            .catch(error => {
                console.error('Failed to get random product:', error);
                this.tryFallbackRecommendations();
            });
    }

    handleProductsData(data) {
        // 处理不同API返回的数据结构
        const product_ids = data.products.map(item => `id:${item.id}`).join(' OR ')
        fetch(`/search?type=product&q=${product_ids}`)
            .then(res => res.text())
            .then(data => {
                const dataHtml = new DOMParser().parseFromString(data, 'text/html');
                const productList = dataHtml.querySelector('.product-grid');
                if (!productList) {
                    return;
                }

                // 获取swiper-wrapper容器
                const swiperWrapper = this.container.querySelector('.swiper-wrapper');
                if (swiperWrapper) {
                    // 将产品项包裹在swiper-slide中
                    const productItems = productList.querySelectorAll('.grid__item');
                    productItems.forEach(item => {
                        const slide = document.createElement('div');
                        slide.className = 'swiper-slide';
                        slide.appendChild(item);
                        swiperWrapper.appendChild(slide);
                    });
                } else {
                    // 如果不是swiper容器，使用原来的方式
                    this.container.innerHTML = productList.outerHTML;
                }

                // 初始化Swiper
                this.initSwiper();
            });
    }

    generateProductsHTML(products) {
        let productsHTML = '';

        products.forEach(product => {
            // 过滤掉已在购物车中的商品（如果购物车不为空）
            if (!this.options.cartItems || this.options.cartItems.length === 0 || !this.options.cartItems.includes(product.id)) {
                productsHTML += this.generateProductHTML(product);
            }
        });

        return productsHTML;
    }

    generateProductHTML(product) {
        const productUrl = `${window.Shopify.routes.root}products/${product.handle}`;
        const imageUrl = product.featured_image || (product.images && product.images[0]) || '';
        const price = this.formatPrice(product);
        const secondaryImage = product.images && product.images.length > 1 ? product.images[1] : null;
        return `
      <div class="${this.options.cssPrefix}__item-wrapper">
        <a href="${productUrl}" class="${this.options.cssPrefix}__item">
          <div class="${this.options.cssPrefix}__media">
            <div class="media media--transparent media--hover-effect">
              ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" class="${this.options.cssPrefix}__item-image motion-reduce" loading="lazy">` : ''}
              ${secondaryImage ? `<img src="${secondaryImage}" alt="" class="${this.options.cssPrefix}__item-image ${this.options.cssPrefix}__item-image--secondary motion-reduce" loading="lazy">` : ''}
            </div>
          </div>
          <div class="${this.options.cssPrefix}__item-info">
            <div class="${this.options.cssPrefix}__item-title">${product.title}</div>
            ${this.options.showPrice ? `<div class="${this.options.cssPrefix}__item-price">${price}</div>` : ''}
          </div>
          <div class="${this.options.cssPrefix}__item-badge">
            <div class="${this.options.cssPrefix}__item-favorites" data-product-id="${product.id}">
              <!-- 收藏按钮将通过JavaScript动态添加 -->
            </div>
          </div>
        </a>
      </div>
    `;
    }

    formatPrice(product) {
        if (!this.options.showPrice) return '';

        try {
            const priceValue = product.price || product.price_min || 0;
            return new Intl.NumberFormat(this.options.shopLocale, {
                style: 'currency',
                currency: this.options.shopCurrency
            }).format(priceValue / 100);
        } catch (error) {
            return product.price || product.price_min || '价格待定';
        }
    }

    initializeFavoriteButtons() {
        const favoriteContainers = this.container.querySelectorAll(`.${this.options.cssPrefix}__item-favorites`);
        favoriteContainers.forEach(container => {
            const productId = container.getAttribute('data-product-id');
            if (productId && typeof window.renderFavoriteButton === 'function') {
                window.renderFavoriteButton(container, productId);
            }
        });
    }

    tryFallbackRecommendations() {
        const fallbackUrl = `${window.Shopify.routes.root}collections/all/products.json?limit=${this.options.limit}`;

        fetch(fallbackUrl)
            .then(response => response.json())
            .then(data => {
                const products = data.products || [];
                if (products && products.length > 0) {
                    const productsHTML = this.generateFallbackProductsHTML(products);

                    if (productsHTML) {
                        this.container.innerHTML = productsHTML;
                        this.initializeFavoriteButtons();
                    } else {
                        this.hideRecommendations();
                    }
                } else {
                    this.hideRecommendations();
                }
            })
            .catch(error => this.hideRecommendations());
    }

    generateFallbackProductsHTML(products) {
        let productsHTML = '';

        products.slice(0, this.options.limit).forEach(product => {
            const productUrl = `${window.Shopify.routes.root}products/${product.handle}`;
            const imageUrl = product.featured_image || (product.images && product.images[0]) || '';
            const price = new Intl.NumberFormat(this.options.shopLocale, {
                style: 'currency',
                currency: this.options.shopCurrency
            }).format(product.price / 100);

            const secondaryImage = product.images && product.images.length > 1 ? product.images[1] : null;

            productsHTML += `
        <div class="${this.options.cssPrefix}__item-wrapper">
          <a href="${productUrl}" class="${this.options.cssPrefix}__item">
            <div class="${this.options.cssPrefix}__media">
              <div class="media media--transparent media--hover-effect">
                ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" class="${this.options.cssPrefix}__item-image motion-reduce" loading="lazy">` : ''}
                ${secondaryImage ? `<img src="${secondaryImage}" alt="" class="${this.options.cssPrefix}__item-image ${this.options.cssPrefix}__item-image--secondary motion-reduce" loading="lazy">` : ''}
              </div>
            </div>
            <div class="${this.options.cssPrefix}__item-info">
              <div class="${this.options.cssPrefix}__item-title">${product.title}</div>
              ${this.options.showPrice ? `<div class="${this.options.cssPrefix}__item-price">${price}</div>` : ''}
            </div>
            <div class="${this.options.cssPrefix}__item-badge">
              <div class="${this.options.cssPrefix}__item-favorites" data-product-id="${product.id}">
                <!-- 收藏按钮将通过JavaScript动态添加 -->
              </div>
            </div>
          </a>
        </div>
      `;
        });

        return productsHTML;
    }

    handleError(error) {
        // 如果是空购物车，尝试备用方案
        if (!this.options.cartItems || this.options.cartItems.length === 0) {
            this.tryFallbackRecommendations();
        } else {
            this.hideRecommendations();
        }
    }

    hideRecommendations() {
        const recommendationsSection = this.container.closest(`.${this.options.cssPrefix}`);
        if (recommendationsSection) {
            recommendationsSection.style.display = 'none';
        }
    }

    /**
     * 初始化Swiper轮播
     */
    initSwiper() {
        // 确保Swiper库已加载
        if (typeof Swiper === 'undefined') {
            console.warn('Swiper library not found');
            return;
        }

        // 销毁已存在的实例
        if (this.swiper) {
            this.swiper.destroy(true, true);
        }

        // 检查是使用滚动条还是箭头
        const scrollbarEl = this.container.querySelector('.swiper-scrollbar');
        const prevButton = this.container.querySelector('.swiper-button-prev');
        const nextButton = this.container.querySelector('.swiper-button-next');
        const useScrollbar = !!scrollbarEl;
        // Swiper 配置
        const swiperConfig = {
            slidesPerView: this.options.slidesPerView || 4,
            spaceBetween: 20,
            loop: !useScrollbar, // 使用滚动条时不循环
            grabCursor: true,
            slidesPerGroup: 2,
            // 响应式断点
            breakpoints: {
                320: {
                    slidesPerView: 2,
                    spaceBetween: 10,
                },
                768: {
                    slidesPerView: 3,
                    spaceBetween: 15,
                },
                1024: {
                    slidesPerView: this.options.slidesPerView || 4,
                    spaceBetween: 20,
                }
            }
        };

        // 如果使用滚动条
        if (useScrollbar) {
            swiperConfig.scrollbar = {
                el: scrollbarEl,
                draggable: true,
            };
        }
        // 否则使用导航箭头
        else if (prevButton && nextButton) {
            swiperConfig.navigation = {
                nextEl: nextButton,
                prevEl: prevButton,
            };
        }

        // 初始化Swiper
        this.swiper = new Swiper(this.container, swiperConfig);
    }

    /**
     * 销毁实例
     */
    destroy() {
        if (this.swiper) {
            this.swiper.destroy(true, true);
            this.swiper = null;
        }
    }
}

// 全局初始化函数
window.initCartRecommendations = function (containerId, options) {
    return new CartRecommendations(containerId, options);
};

// DOM加载完成后自动初始化
document.addEventListener('DOMContentLoaded', function () {
    // 查找所有推荐容器并自动初始化
    const recommendationContainers = document.querySelectorAll('[id^="cart-recommendations-"]');

    recommendationContainers.forEach(container => {
        const containerId = container.id;
        const cssPrefix = containerId.replace('cart-recommendations-', '');
        // 从容器的data属性获取配置
        const options = {
            limit: parseInt(container.dataset.limit) || 8,
            showPrice: container.dataset.showPrice !== 'false',
            slidesPerView: parseInt(container.dataset.slidesPerView) || 4,
            showScrollbar: container.dataset.showScrollbar === 'true',
            cssPrefix: 'recommendations',
            cartItems: window.cartItems || [],
            shopLocale: window.Shopify?.locale || 'en',
            shopCurrency: window.Shopify?.currency?.active || 'USD'
        };

        new CartRecommendations(containerId, options);
    });
});